import React, { useEffect, useState } from 'react';
import httpService from "../../services/httpService";
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import EpisodeUserItem from './EpisodeUserItem';
import Image from "react-bootstrap/Image";
import MultiBarChart from "../charts/MultiBarChart";

const EpisodeUsersData = ({ episode, clubGuid }) => {
    const [loading, setLoading] = useState(true);
    const [usersSummary, setUsersSummary] = useState([]);
    const [error, setError] = useState(null);
    const [charts, setCharts] = useState(null);
    const [expandedUserGuid, setExpandedUserGuid] = useState(null);

    useEffect(() => {
        if (episode && episode.guid) {
            fetchUsersSummary();
        }
    }, [episode]);

    const fetchUsersSummary = () => {
        setLoading(true);
        setError(null);

        httpService.get(`/dash/episode/${episode.guid}/users/summary`)
            .then((res) => {
                console.log('Episode users summary:', res.data);
                setUsersSummary(res.data.users || []);
                setCharts(res.data.charts || []);
            })
            .catch((err) => {
                console.error('Error fetching users summary:', err);
                setError(err.response?.data?.message || "Error loading users data");
            })
            .finally(() => setLoading(false));
    };

    const handleToggleUser = (userGuid) => {
        // If clicking the same user, collapse it; otherwise expand the new one
        setExpandedUserGuid(prev => prev === userGuid ? null : userGuid);
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-2">Loading users data...</p>
            </div>
        );
    }

    if (error) {
        return (
            <Alert variant="danger">
                {error}
            </Alert>
        );
    }

    if (!usersSummary || usersSummary.length === 0) {
        return (
            <Alert variant="info">
                No users data available for this episode.
            </Alert>
        );
    }

    return (
        <div>
            <Image fluid src={'/img/audience-wide.jpg'} className="mb-3 fluid" />
            <h6 className="mb-3"><strong>{episode.title}</strong></h6>
            {/* Summary Statistics */}
            <div className="my-3">
                <div className="row">
                    <div className="col-md-4">
                        <div className="card text-center">
                            <div className="card-body">
                                <h5 className="card-title">{usersSummary.length}</h5>
                                <p className="card-text text-muted">Total Users</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card text-center">
                            <div className="card-body">
                                <h5 className="card-title">
                                    {usersSummary.reduce((sum, u) => sum + u.stats.total_practices, 0)}
                                </h5>
                                <p className="card-text text-muted">Total Practices</p>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card text-center">
                            <div className="card-body">
                                <h5 className="card-title">
                                    {usersSummary.reduce((sum, u) => sum + u.stats.completed_practices, 0)}
                                </h5>
                                <p className="card-text text-muted">Completed Practices</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={'my-3'}>
                <MultiBarChart data={charts[0]} />
            </div>

            {/* User List with Expandable Cards */}
            <div className="mb-4">
                {usersSummary.map((userSummary, index) => {
                    let elementIndex;
                    if (index === 0) {
                        elementIndex = 'first';
                    } else if (index === usersSummary.length - 1) {
                        elementIndex = 'last';
                    } else {
                        elementIndex = 'middle';
                    }

                    return (
                        <EpisodeUserItem
                            key={userSummary.user.id}
                            userGuid={userSummary.user.guid}
                            episodeGuid={episode.guid}
                            clubGuid={clubGuid}
                            userSummary={userSummary}
                            isExpanded={expandedUserGuid === userSummary.user.guid}
                            onToggle={() => handleToggleUser(userSummary.user.guid)}
                            elementIndex={elementIndex}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default EpisodeUsersData;